import React, { useState, useContext, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  makeStyles,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import {
  CheckCircle as AcceptIcon,
  Close as CloseIcon,
  SwapHoriz as TransferIcon,
  Replay as ReturnIcon,
  DoneAll as ResolveIcon,
  Schedule as ScheduleIcon,
  CheckBox as SelectIcon,
  SelectAll as SelectAllIcon,
  Clear as ClearSelectionIcon,
  MailOutline as MarkUnreadIcon,
  // Mesmo sino cortado do WhatsApp: serve tanto para "resetar alarme" quanto
  // para "silenciar" (o que distingue os dois é a cor e o rótulo).
  NotificationsOff as NotificationsOffIcon,
  NotificationsActive as UnmuteIcon,
  ArrowRight as ArrowRightIcon,
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import { useMutedContacts } from "../../context/MutedContacts/MutedContactsContext";
import useSettings from "../../hooks/useSettings";
import useCloseTicketGuard from "../../hooks/useCloseTicketGuard";
import useTicketCloseRules from "../../hooks/useTicketCloseRules";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ScheduleModal from "../ScheduleModal";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  menuItem: {
    minWidth: 200,
  },
  menuIcon: {
    minWidth: 36,
  },
  divider: {
    margin: "8px 0",
  },
  menuTitle: {
    padding: "8px 16px",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    textTransform: "uppercase",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: 16,
  },
  submenuArrow: {
    marginLeft: 8,
    color: theme.palette.text.secondary,
  },
}));

const TicketContextMenu = ({
  anchorPosition,
  open,
  onClose,
  ticket,
  selectedTickets,
  onSelectionModeChange,
  selectionMode,
  onSelectTicket,
  onSelectAll,
  onClearSelection,
  tickets,
  currentStatus,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [transferTargetTicket, setTransferTargetTicket] = useState(null);
  const [transferTargetTicketId, setTransferTargetTicketId] = useState(null);
  const [transferTargetTickets, setTransferTargetTickets] = useState(null);
  const [resetAlarmEnabled, setResetAlarmEnabled] = useState(false);
  const [muteAnchorEl, setMuteAnchorEl] = useState(null);

  const { isContactMuted, muteContact, unmuteContact } = useMutedContacts();

  const { getAll: getAllSettings } = useSettings();
  const { getSetting } = useContext(SettingsContext);
  // Aviso/bloqueio de finalização sem interação com o contato (config da empresa)
  const { closeTicket, dialog: closeGuardDialog } = useCloseTicketGuard();
  // Pergunta de conversão e card no Kanban (obrigatório/opcional) antes de
  // fechar — as mesmas regras do botão Resolver da tela do atendimento. Em
  // ação em massa cada pergunta aparece uma vez e a resposta vale para o lote.
  const { prepareClose, dialogs: closeRulesDialogs } = useTicketCloseRules();

  useEffect(() => {
    // Rebusca a configuração toda vez que o menu abre, para refletir sempre o
    // valor atual (o componente fica montado a sessão inteira; sem isso, uma
    // mudança na tela de Configurações só valeria após recarregar a página).
    if (!open) return undefined;
    let isMounted = true;
    (async () => {
      try {
        const settings = await getAllSettings();
        const setting = settings.find((s) => s.key === "attendanceAlarmResetEnabled");
        if (isMounted) {
          setResetAlarmEnabled(setting?.value === "enabled");
        }
      } catch (err) {
        // silencioso: se falhar, a opção apenas não aparece
        if (isMounted) {
          setResetAlarmEnabled(false);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // O submenu de silenciar não pode sobreviver ao menu que o ancorou.
  useEffect(() => {
    if (!open) setMuteAnchorEl(null);
  }, [open]);

  // Lote em andamento. Se a lista sai da tela no meio dele (aba Resolvidos/Busca,
  // termo digitado na busca, outra página), o lote para antes do próximo chamado;
  // a pergunta que estiver aberta conta como cancelada (cleanup dos hooks).
  const activeBatchRef = useRef(null);
  useEffect(
    () => () => {
      if (activeBatchRef.current) {
        activeBatchRef.current.cancelled = true;
        activeBatchRef.current.interrupted = true;
      }
    },
    []
  );

  const isBulkAction = selectedTickets && selectedTickets.length > 0;
  const targetTickets = isBulkAction ? selectedTickets : (ticket ? [ticket] : []);
  const ticketStatus = ticket?.status || currentStatus;
  const isPending = ticketStatus === "pending";
  // Transferir chamado em Aguardando (setting transferPendingTickets, ao vivo pelo socket)
  const canTransferPending =
    isPending && getSetting("transferPendingTickets") === "enabled";
  const isOpen = ticketStatus === "open";
  const hasUnreadIndicator = ticket?.markedAsUnread || (ticket?.unreadMessages > 0);
  const hasAlarmInSelection = isBulkAction
    ? targetTickets.some((t) => t?.isInAlarm)
    : !!ticket?.isInAlarm;

  // Silenciar vale para o CONTATO do ticket. Em grupo o contato do ticket é o
  // próprio grupo — mas grupos hoje já não geram alerta nenhum (ver o guard de
  // isGroup no NotificationsPopOver), então a opção fica escondida neles para
  // não prometer algo sem efeito.
  const muteContactId = ticket?.contact?.id || ticket?.contactId;
  const canMute = !isBulkAction && !ticket?.isGroup && !!muteContactId;
  const isMuted = canMute && isContactMuted(muteContactId);

  // Aceitar ticket (apenas para pending)
  const handleAcceptTicket = async (ticketToAccept) => {
    try {
      // Verificar se o ticket tem fila e se o usuário tem acesso a ela
      if (ticketToAccept.queueId) {
        const userQueueIds = user.queues.map((q) => q.id);
        if (!userQueueIds.includes(ticketToAccept.queueId)) {
          toast.error(i18n.t("tickets.toasts.noQueueAccess"));
          return false;
        }
      }
      
      await api.put(`/tickets/${ticketToAccept.id}`, {
        status: "open",
        userId: user?.id,
      });
      return true;
    } catch (err) {
      toastError(err);
      return false;
    }
  };

  // Finalizar ticket — closeTicket cuida do aviso/bloqueio de finalização sem interação
  // e da pergunta da mensagem de conclusão (`batch` faz as respostas valerem para o lote).
  // Em ação em massa o erro fica por conta do resumo (showError: false).
  const handleCloseTicket = async (ticketToClose, batch) => {
    const { proceed, converted } = await prepareClose(ticketToClose, batch);
    // Lote interrompido (a lista saiu da tela) enquanto as regras rodavam
    if (!proceed || batch?.cancelled) return false;

    return closeTicket(
      ticketToClose.id,
      {
        userId: user?.id,
        queueId: ticketToClose?.queue?.id || ticketToClose?.queueId,
        useIntegration: false,
        promptId: null,
        integrationId: null,
        ...(converted ? { converted } : {}),
      },
      { showError: !isBulkAction, batch }
    );
  };

  // Retornar para fila (pending)
  const handleReturnToQueue = async (ticketToReturn) => {
    try {
      await api.put(`/tickets/${ticketToReturn.id}`, {
        status: "pending",
        userId: null,
      });
      return true;
    } catch (err) {
      toastError(err);
      return false;
    }
  };

  // Resolver ticket (fechar)
  const handleResolveTicket = async (ticketToResolve, batch) => {
    const { proceed, converted } = await prepareClose(ticketToResolve, batch);
    // Lote interrompido (a lista saiu da tela) enquanto as regras rodavam
    if (!proceed || batch?.cancelled) return false;

    return closeTicket(
      ticketToResolve.id,
      {
        userId: user?.id,
        queueId: ticketToResolve?.queue?.id || ticketToResolve?.queueId,
        ...(converted ? { converted } : {}),
      },
      { showError: !isBulkAction, batch }
    );
  };

  // Marcar como não lido
  const handleMarkAsUnread = async (ticketToMark) => {
    try {
      await api.put(`/tickets/${ticketToMark.id}/mark-unread`, {
        markedAsUnread: true,
      });
      return true;
    } catch (err) {
      toastError(err);
      return false;
    }
  };

  // Marcar como lido
  const handleMarkAsRead = async (ticketToMark) => {
    try {
      await api.put(`/tickets/${ticketToMark.id}/mark-unread`, {
        markedAsUnread: false,
      });
      return true;
    } catch (err) {
      toastError(err);
      return false;
    }
  };

  // Resetar alarme (dar OK no alarme sem enviar mensagem ao cliente)
  const handleResetAlarmTicket = async (ticketToReset) => {
    try {
      await api.put(`/tickets/${ticketToReset.id}/reset-alarm`);
      return true;
    } catch (err) {
      toastError(err);
      return false;
    }
  };

  // Executar ação em massa
  const executeBulkAction = async (actionFn, actionName) => {
    setLoading(true);
    let successCount = 0;
    let failCount = 0;
    // Respostas compartilhadas pelo lote (convertido, card, sem interação,
    // mensagem de conclusão): cada pergunta aparece uma vez e vale para todos
    const batch = {};
    activeBatchRef.current = batch;

    for (const t of targetTickets) {
      // Cancelar uma pergunta também vale para o lote: os restantes ficam como estão
      if (batch.cancelled) break;
      const success = await actionFn(t, batch);
      if (success) {
        successCount++;
      } else if (!batch.cancelled) {
        failCount++;
      }
    }

    activeBatchRef.current = null;

    // A lista saiu da tela: este componente já foi desmontado, então para em
    // silêncio, sem resumo e sem mexer em estado
    if (batch.interrupted) return;

    setLoading(false);
    
    if (successCount > 0) {
      toast.success(
        i18n.t("ticketContextMenu.bulkActionSuccess", {
          count: successCount,
          action: actionName,
        })
      );
    }
    
    if (failCount > 0) {
      toast.error(
        i18n.t("ticketContextMenu.bulkActionFail", {
          count: failCount,
        })
      );
    }

    const untouchedCount = targetTickets.length - successCount - failCount;
    if (batch.cancelled && untouchedCount > 0) {
      toast.info(
        i18n.t("ticketContextMenu.bulkActionCancelled", {
          count: untouchedCount,
        })
      );
    }

    onClearSelection && onClearSelection();
    onClose();
  };

  // Handlers de ações
  const handleAccept = () => {
    if (isBulkAction) {
      setConfirmTitle(i18n.t("ticketContextMenu.confirmAcceptTitle"));
      setConfirmMessage(
        i18n.t("ticketContextMenu.confirmAcceptMessage", {
          count: targetTickets.length,
        })
      );
      setConfirmAction(() => () => executeBulkAction(handleAcceptTicket, i18n.t("ticketContextMenu.actions.accept")));
      setConfirmModalOpen(true);
    } else {
      handleAcceptTicket(ticket).then((success) => {
        if (success) {
          history.push(`/tickets/${ticket.uuid}`);
        }
        onClose();
      });
    }
  };

  const handleClose = () => {
    if (isBulkAction) {
      setConfirmTitle(i18n.t("ticketContextMenu.confirmCloseTitle"));
      setConfirmMessage(
        i18n.t("ticketContextMenu.confirmCloseMessage", {
          count: targetTickets.length,
        })
      );
      setConfirmAction(() => () => executeBulkAction(handleCloseTicket, i18n.t("ticketContextMenu.actions.close")));
      setConfirmModalOpen(true);
    } else {
      // Fecha o menu antes: os diálogos das regras (e o modal do card) abrem
      // por cima da lista, não do menu
      onClose();
      handleCloseTicket(ticket);
    }
  };

  const handleReturn = () => {
    if (isBulkAction) {
      setConfirmTitle(i18n.t("ticketContextMenu.confirmReturnTitle"));
      setConfirmMessage(
        i18n.t("ticketContextMenu.confirmReturnMessage", {
          count: targetTickets.length,
        })
      );
      setConfirmAction(() => () => executeBulkAction(handleReturnToQueue, i18n.t("ticketContextMenu.actions.return")));
      setConfirmModalOpen(true);
    } else {
      handleReturnToQueue(ticket).then(() => {
        onClose();
      });
    }
  };

  const handleResolve = () => {
    if (isBulkAction) {
      setConfirmTitle(i18n.t("ticketContextMenu.confirmResolveTitle"));
      setConfirmMessage(
        i18n.t("ticketContextMenu.confirmResolveMessage", {
          count: targetTickets.length,
        })
      );
      setConfirmAction(() => () => executeBulkAction(handleResolveTicket, i18n.t("ticketContextMenu.actions.resolve")));
      setConfirmModalOpen(true);
    } else {
      // Fecha o menu antes: os diálogos das regras (e o modal do card) abrem
      // por cima da lista, não do menu
      onClose();
      handleResolveTicket(ticket);
    }
  };

  const handleTransfer = () => {
    if (!isBulkAction && ticket?.id) {
      setTransferTargetTicket(ticket);
      setTransferTargetTicketId(ticket.id);
      setTransferModalOpen(true);
    } else if (isBulkAction && targetTickets?.length > 0 && targetTickets[0]?.id) {
      // Em massa o modal transfere todos; o primeiro só alimenta os rótulos
      setTransferTargetTicket(targetTickets[0]);
      setTransferTargetTicketId(targetTickets[0].id);
      setTransferTargetTickets([...targetTickets]);
      setTransferModalOpen(true);
    } else {
      toastError(i18n.t("ticketContextMenu.errors.transferTicketNotFound"));
    }
    onClose();
  };

  const handleSchedule = () => {
    if (!isBulkAction && ticket) {
      setScheduleModalOpen(true);
    }
    onClose();
  };

  const handleMarkUnread = () => {
    if (isBulkAction) {
      setConfirmTitle(i18n.t("ticketContextMenu.confirmMarkUnreadTitle"));
      setConfirmMessage(
        i18n.t("ticketContextMenu.confirmMarkUnreadMessage", {
          count: targetTickets.length,
        })
      );
      setConfirmAction(() => () => executeBulkAction(handleMarkAsUnread, i18n.t("ticketContextMenu.actions.markUnread")));
      setConfirmModalOpen(true);
    } else {
      handleMarkAsUnread(ticket).then(() => {
        onClose();
      });
    }
  };

  const handleMarkRead = () => {
    if (isBulkAction) {
      setConfirmTitle(i18n.t("ticketContextMenu.confirmMarkReadTitle"));
      setConfirmMessage(
        i18n.t("ticketContextMenu.confirmMarkReadMessage", {
          count: targetTickets.length,
        })
      );
      setConfirmAction(() => () => executeBulkAction(handleMarkAsRead, i18n.t("ticketContextMenu.actions.markRead")));
      setConfirmModalOpen(true);
    } else {
      handleMarkAsRead(ticket).then(() => {
        onClose();
      });
    }
  };

  const handleResetAlarm = () => {
    if (isBulkAction) {
      executeBulkAction(handleResetAlarmTicket, i18n.t("ticketContextMenu.actions.resetAlarm"));
    } else {
      handleResetAlarmTicket(ticket).then(() => {
        onClose();
      });
    }
  };

  // Silenciar: submenu próprio, porque o Menu do MUI v4 não tem submenu nativo.
  const handleOpenMuteMenu = (event) => {
    setMuteAnchorEl(event.currentTarget);
  };

  const handleCloseMuteMenu = () => {
    setMuteAnchorEl(null);
  };

  const handleMute = (duration) => {
    muteContact(muteContactId, duration);
    handleCloseMuteMenu();
    onClose();
  };

  const handleUnmute = () => {
    unmuteContact(muteContactId);
    onClose();
  };

  const handleSelect = () => {
    if (ticket) {
      onSelectionModeChange && onSelectionModeChange(true, ticket.status);
      onSelectTicket && onSelectTicket(ticket);
    }
    onClose();
  };

  const handleSelectAll = () => {
    onSelectionModeChange && onSelectionModeChange(true, currentStatus || ticket?.status);
    onSelectAll && onSelectAll();
    onClose();
  };

  const handleClearSelection = () => {
    onClearSelection && onClearSelection();
    onSelectionModeChange && onSelectionModeChange(false, null);
    onClose();
  };

  const handleTransferModalClose = () => {
    setTransferModalOpen(false);
    setTransferTargetTicket(null);
    setTransferTargetTicketId(null);
    setTransferTargetTickets(null);
    onClearSelection && onClearSelection();
  };

  const handleScheduleModalClose = () => {
    setScheduleModalOpen(false);
  };

  const handleConfirmModalClose = () => {
    setConfirmModalOpen(false);
    setConfirmAction(null);
  };

  const handleConfirmAction = () => {
    if (confirmAction) {
      confirmAction();
    }
    setConfirmModalOpen(false);
  };

  // Bloquear menu de contexto nativo do navegador dentro do nosso menu
  const handlePreventContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      {closeGuardDialog}
      {closeRulesDialogs}
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition}
        open={open}
        onClose={onClose}
        getContentAnchorEl={null}
        onContextMenu={handlePreventContextMenu}
        BackdropProps={{
          invisible: true,
          onContextMenu: (e) => {
            e.preventDefault();
            onClose();
          },
        }}
        MenuListProps={{
          onContextMenu: handlePreventContextMenu,
        }}
      >
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress size={24} />
          </div>
        ) : [
            /* Título da seção de seleção */
            <Typography key="selection-title" className={classes.menuTitle}>
              {i18n.t("ticketContextMenu.selection")}
            </Typography>,

            /* Opções de seleção */
            !selectionMode && (
              <MenuItem key="select-item" onClick={handleSelect} className={classes.menuItem}>
                <ListItemIcon className={classes.menuIcon}>
                  <SelectIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={i18n.t("ticketContextMenu.select")} />
              </MenuItem>
            ),

            <MenuItem key="select-all" onClick={handleSelectAll} className={classes.menuItem}>
              <ListItemIcon className={classes.menuIcon}>
                <SelectAllIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={i18n.t("ticketContextMenu.selectAll")} />
            </MenuItem>,

            selectionMode && selectedTickets.length > 0 && (
              <MenuItem key="clear-selection" onClick={handleClearSelection} className={classes.menuItem}>
                <ListItemIcon className={classes.menuIcon}>
                  <ClearSelectionIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={i18n.t("ticketContextMenu.clearSelection")}
                  secondary={`${selectedTickets.length} ${i18n.t("ticketContextMenu.selected")}`}
                />
              </MenuItem>
            ),

            <Divider key="divider-1" className={classes.divider} />,

            /* Título da seção de ações */
            <Typography key="actions-title" className={classes.menuTitle}>
              {i18n.t("ticketContextMenu.actions.title")}
              {isBulkAction && ` (${selectedTickets.length})`}
            </Typography>,

            /* Ações para tickets em "Aguardando" (pending) */
            isPending && (
              <MenuItem key="accept-action" onClick={handleAccept} className={classes.menuItem}>
                <ListItemIcon className={classes.menuIcon}>
                  <AcceptIcon fontSize="small" style={{ color: "#4caf50" }} />
                </ListItemIcon>
                <ListItemText primary={i18n.t("ticketContextMenu.actions.accept")} />
              </MenuItem>
            ),

            isPending && (
              <MenuItem key="close-action" onClick={handleClose} className={classes.menuItem}>
                <ListItemIcon className={classes.menuIcon}>
                  <CloseIcon fontSize="small" style={{ color: "#f44336" }} />
                </ListItemIcon>
                <ListItemText primary={i18n.t("ticketContextMenu.actions.close")} />
              </MenuItem>
            ),

            /* Transferir: sempre em "Atendendo"; em "Aguardando" só com a setting ligada */
            (isOpen || canTransferPending) && (
              <MenuItem key="transfer-action" onClick={handleTransfer} className={classes.menuItem}>
                <ListItemIcon className={classes.menuIcon}>
                  <TransferIcon fontSize="small" style={{ color: "#2196f3" }} />
                </ListItemIcon>
                <ListItemText primary={i18n.t("ticketContextMenu.actions.transfer")} />
              </MenuItem>
            ),

            /* Ações para tickets em "Atendendo" (open) */
            isOpen && (
              <MenuItem key="return-action" onClick={handleReturn} className={classes.menuItem}>
                <ListItemIcon className={classes.menuIcon}>
                  <ReturnIcon fontSize="small" style={{ color: "#ff9800" }} />
                </ListItemIcon>
                <ListItemText primary={i18n.t("ticketContextMenu.actions.return")} />
              </MenuItem>
            ),

            isOpen && (
              <MenuItem key="resolve-action" onClick={handleResolve} className={classes.menuItem}>
                <ListItemIcon className={classes.menuIcon}>
                  <ResolveIcon fontSize="small" style={{ color: "#4caf50" }} />
                </ListItemIcon>
                <ListItemText primary={i18n.t("ticketContextMenu.actions.resolve")} />
              </MenuItem>
            ),

            isOpen && !isBulkAction && (
              <MenuItem key="schedule-action" onClick={handleSchedule} className={classes.menuItem}>
                <ListItemIcon className={classes.menuIcon}>
                  <ScheduleIcon fontSize="small" style={{ color: "#9c27b0" }} />
                </ListItemIcon>
                <ListItemText primary={i18n.t("ticketContextMenu.actions.schedule")} />
              </MenuItem>
            ),

            /* Ação de resetar alarme - só quando habilitado e o ticket está em alarme */
            resetAlarmEnabled && hasAlarmInSelection && (
              <MenuItem key="reset-alarm-action" onClick={handleResetAlarm} className={classes.menuItem}>
                <ListItemIcon className={classes.menuIcon}>
                  <NotificationsOffIcon fontSize="small" style={{ color: "#f44336" }} />
                </ListItemIcon>
                <ListItemText primary={i18n.t("ticketContextMenu.actions.resetAlarm")} />
              </MenuItem>
            ),

            /* Ação de marcar como lido/não lido - disponível para todos os status */
            !hasUnreadIndicator ? (
              <MenuItem key="mark-unread-action" onClick={handleMarkUnread} className={classes.menuItem}>
                <ListItemIcon className={classes.menuIcon}>
                  <MarkUnreadIcon fontSize="small" style={{ color: "#ff5722" }} />
                </ListItemIcon>
                <ListItemText primary={i18n.t("ticketContextMenu.actions.markUnread")} />
              </MenuItem>
            ) : (
              <MenuItem key="mark-read-action" onClick={handleMarkRead} className={classes.menuItem}>
                <ListItemIcon className={classes.menuIcon}>
                  <MarkUnreadIcon fontSize="small" style={{ color: "#4caf50" }} />
                </ListItemIcon>
                <ListItemText primary={i18n.t("ticketContextMenu.actions.markRead")} />
              </MenuItem>
            ),

            /* Silenciar / reativar som do contato (escondido em grupo e em massa) */
            canMute && !isMuted && (
              <MenuItem key="mute-action" onClick={handleOpenMuteMenu} className={classes.menuItem}>
                <ListItemIcon className={classes.menuIcon}>
                  <NotificationsOffIcon fontSize="small" style={{ color: "#607d8b" }} />
                </ListItemIcon>
                <ListItemText primary={i18n.t("ticketContextMenu.actions.mute")} />
                <ArrowRightIcon fontSize="small" className={classes.submenuArrow} />
              </MenuItem>
            ),

            canMute && isMuted && (
              <MenuItem key="unmute-action" onClick={handleUnmute} className={classes.menuItem}>
                <ListItemIcon className={classes.menuIcon}>
                  <UnmuteIcon fontSize="small" style={{ color: "#4caf50" }} />
                </ListItemIcon>
                <ListItemText primary={i18n.t("ticketContextMenu.actions.unmute")} />
              </MenuItem>
            )
        ].filter(Boolean)}
      </Menu>

      {/* Submenu de duração do silenciar (MUI v4 não tem submenu nativo) */}
      <Menu
        anchorEl={muteAnchorEl}
        open={Boolean(muteAnchorEl)}
        onClose={handleCloseMuteMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onContextMenu={handlePreventContextMenu}
        MenuListProps={{ onContextMenu: handlePreventContextMenu }}
      >
        <MenuItem onClick={() => handleMute("8h")} className={classes.menuItem}>
          <ListItemText primary={i18n.t("ticketContextMenu.actions.muteFor8Hours")} />
        </MenuItem>
        <MenuItem onClick={() => handleMute("1w")} className={classes.menuItem}>
          <ListItemText primary={i18n.t("ticketContextMenu.actions.muteFor1Week")} />
        </MenuItem>
        <MenuItem onClick={() => handleMute("forever")} className={classes.menuItem}>
          <ListItemText primary={i18n.t("ticketContextMenu.actions.muteForever")} />
        </MenuItem>
      </Menu>

      {/* Modal de transferência */}
      {transferModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferModalOpen}
          onClose={handleTransferModalClose}
          ticketid={transferTargetTicketId}
          ticket={transferTargetTicket}
          bulkTickets={transferTargetTickets}
        />
      )}

      {/* Modal de agendamento */}
      {scheduleModalOpen && ticket && (
        <ScheduleModal
          open={scheduleModalOpen}
          onClose={handleScheduleModalClose}
          contactId={ticket?.contact?.id}
          reload={() => {}}
        />
      )}

      {/* Modal de confirmação */}
      <ConfirmationModal
        title={confirmTitle}
        open={confirmModalOpen}
        onClose={handleConfirmModalClose}
        onConfirm={handleConfirmAction}
      >
        {confirmMessage}
      </ConfirmationModal>
    </>
  );
};

export default TicketContextMenu;
